import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { HttpHelper } from '@umss/shared/utils';
import { Observable } from 'rxjs';
import { PagedResultDto } from '../models/paged-result.model';
import {
  FacilityInvoice,
  FacilityInvoiceDetail,
  InvoiceDto,
  InvoicePayment,
  InvoicesSummary,
  InvoiceSummaryDto,
  RecordInvoiceDto
} from '../models/payments/invoices.dto';
import { StripePaymentLink } from '../models/stripe.model';
import {
  CreateCheckoutSessionRequest,
  StripeCheckoutSession
} from '../models/payments/checkout-session.dto';

@Injectable({ providedIn: 'root' })
export class PaymentInvoicesService {
  private readonly baseUrl = 'api/Payments/invoices';

  private readonly http = inject(HttpClient);

  /**
   * Fetch invoices for a facility.
   * @param payload Optional filters like status, date range, etc.
   * @returns An Observable of paginated FacilityInvoiceDetail records.
   */
  getAll(
    payload?: Partial<InvoiceDto>
  ): Observable<PagedResultDto<FacilityInvoice>> {
    const params = HttpHelper.toHttpParams(payload ?? {});

    return this.http.get<PagedResultDto<FacilityInvoice>>(this.baseUrl, {
      params
    });
  }

  getSummary(dto?: InvoiceSummaryDto): Observable<InvoicesSummary> {
    const params = HttpHelper.toHttpParams(dto ?? {});
    return this.http.get<InvoicesSummary>(
      `${this.baseUrl}/pay-period-summary`,
      { params }
    );
  }

  /**
   * Get a single invoice by its ID.
   * @param invoiceId The ID of the invoice.
   * @returns An Observable of the Invoice.
   */
  get(invoiceId: string): Observable<FacilityInvoiceDetail> {
    return this.http.get<FacilityInvoiceDetail>(`${this.baseUrl}/${invoiceId}`);
  }

  recordPayment(
    invoiceId: string,
    body: RecordInvoiceDto
  ): Observable<InvoicePayment> {
    return this.http.post<InvoicePayment>(
      `${this.baseUrl}/${invoiceId}/payments`,
      body
    );
  }

  getPayments(invoiceId: string): Observable<InvoicePayment[]> {
    return this.http.get<InvoicePayment[]>(
      `${this.baseUrl}/${invoiceId}/payments`
    );
  }

  download(invoiceId: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/${invoiceId}/download`, {
      responseType: 'blob'
    });
  }

  createPaymentLink(invoiceId: string): Observable<StripePaymentLink> {
    return this.http.post<StripePaymentLink>(
      `${this.baseUrl}/${invoiceId}/payment-link`,
      {}
    );
  }

  payInvoiceWithBankAccount(
    invoiceId: string,
    paymentMethodId: string
  ): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${invoiceId}/pay`, {
      paymentMethodId
    });
  }

  createCheckoutSession(
    invoiceId: string,
    body: CreateCheckoutSessionRequest
  ): Observable<StripeCheckoutSession> {
    return this.http.post<StripeCheckoutSession>(
      `${this.baseUrl}/${invoiceId}/checkout-session`,
      body
    );
  }
}
